export const encodeUtf8ToBase64Url = (value: string): string => {
    const bytes = encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_, p1: string) =>
        String.fromCharCode(parseInt(p1, 16)),
    );
    return btoa(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

/**
 * Encode a UTF-8 string (e.g. HTML content) to standard base64 for safe API transport.
 * Avoids Cloud Armor WAF / OWASP CRS rules that block raw HTML in request bodies.
 * Returns the original string unchanged on error or if input is empty.
 */
export function encodeHtmlForTransport(str: string): string {
    if (typeof str !== 'string' || str.length === 0) {
        return str;
    }
    try {
        const bytes = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1: string) =>
            String.fromCharCode(parseInt(p1, 16)),
        );
        return btoa(bytes);
    } catch {
        return str;
    }
}

/**
 * Decode a base64 string back to the original UTF-8 string (e.g. HTML content).
 * If the input does not look like base64, returns it unchanged so that plain-text
 * values stored before encoding was introduced continue to display correctly.
 */
export function decodeHtmlFromTransport(encoded: string): string {
    if (typeof encoded !== 'string' || encoded.length === 0) {
        return encoded;
    }
    if (!looksLikeBase64(encoded)) {
        return encoded;
    }
    try {
        const bytes = atob(encoded);
        return decodeURIComponent(
            bytes
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join(''),
        );
    } catch {
        return encoded;
    }
}

/** Returns true when a string looks like base64 (no HTML angle brackets, valid charset). */
function looksLikeBase64(s: string): boolean {
    return /^[A-Za-z0-9+/=]+$/.test(s) && s.length >= 4 && !s.includes('<') && !s.includes('>');
}
